import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { UserProfile, UserState, InterfaceData, UserPermissions } from './types'
import interfaceApi from '@/api/admin/interfaceApi'
import usersApi from '@/api/identity/usersApi'
import { InterfaceItem, setInterfacesAll } from '../interface/interfaceSlice'
import { InterfaceByOrganization } from '../interfaceByOrganization/interfaceByOrganizationSlice'
import { setInterfacesByOrganization } from '../interfaceByOrganization/interfaceByOrganizationSlice'
import _ from 'lodash'
import { INTERFACE_PERMISSION, UserRole } from '@/types/user'

const initialPermissions: UserPermissions = {
  interfaces: [],
  interfacesByOrganization: [],
  isViewOnly: false,
  isLoading: false,
  lastFetched: null,
}

const initialState: UserState = {
  user: null,
  permissions: initialPermissions,
  isLoading: false,
  error: null,
}

export const fetchUserPermissions = createAsyncThunk(
  'user/fetchPermissions',
  async (interfaceId: number, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as {
        user: UserState
        interface: { interfacesAll: InterfaceItem[] }
        interfaceByOrganization: { data: InterfaceByOrganization[] }
      }
      const user = state.user.user

      if (!user) {
        return rejectWithValue(
          'User data not available. Please ensure user is logged in.',
        )
      }

      let interfaces: InterfaceItem[] = []
      let interfacesByOrganization: InterfaceItem[] = []
      const { data: myAccessControl } = await usersApi.getMyAccessControl()

      if (state.interface.interfacesAll.length > 0) {
        interfaces = state.interface.interfacesAll
      } else {
        const { data } = await interfaceApi.getAll({ PageSize: 0 })
        interfaces = data?.data || []
        dispatch(setInterfacesAll(interfaces))
      }

      let interfacesByOrg: InterfaceByOrganization[] = []
      if (state.interfaceByOrganization.data.length > 0) {
        interfacesByOrg = state.interfaceByOrganization.data
      } else {
        const { data } = await interfaceApi.getMyInterface()
        interfacesByOrg = data || []
        dispatch(setInterfacesByOrganization(interfacesByOrg))
      }

      interfacesByOrganization = interfaces.filter((iface: InterfaceItem) =>
        interfacesByOrg.some((orgInterface) => orgInterface.id === iface.id),
      )
      let allInterfacesOrganizations =
        _.flatten(
          myAccessControl?.organizations.map((item) => item.interfaces),
        ) || []

      let isViewOnly = true
      if (user) {
        try {
          if ([UserRole.Admin, UserRole.SuperAdmin].includes(user.role as UserRole)) {
            isViewOnly = false
          } else {
            const currentInterface = allInterfacesOrganizations.find(
              (i) => Number(i.id) === interfaceId,
            )
            if (!currentInterface) {
              isViewOnly = true
            } else {
              isViewOnly = currentInterface.permissions.every((key) =>
                [INTERFACE_PERMISSION.READ].includes(key),
              )
            }
          }
        } catch (e) {
          console.error('Error calculating isViewOnly:', e)
          isViewOnly = true
        }
      }

      return {
        interfaces,
        interfacesByOrganization,
        isViewOnly,
      }
    } catch (error: any) {
      console.error('Error fetching interfaces:', error)
      return rejectWithValue(error.message || 'Failed to fetch permissions')
    }
  },
)

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setUser: (state, action: PayloadAction<UserProfile>) => {
      state.user = action.payload
      state.error = null
      state.isLoading = false
    },
    updateUserProfile: (state, action: PayloadAction<Partial<UserProfile>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
    updateUserMeasure: (
      state,
      action: PayloadAction<{ id: number; name: string }>,
    ) => {
      if (state.user) {
        state.user.measure = action.payload
      }
    },
    updateUserTimezone: (
      state,
      action: PayloadAction<{ gmtTimeZoneId: number; timeZone?: string }>,
    ) => {
      if (state.user) {
        const timezone = state.user.gmtTimeZone
        if (timezone && timezone.id !== action.payload.gmtTimeZoneId) {
          state.user.gmtTimeZone = {
            ...timezone,
            id: action.payload.gmtTimeZoneId,
          }
        }
        if (action.payload.timeZone) {
          state.user.timeZone = action.payload.timeZone
        }
      }
    },
    updateUserLanguage: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user.language = action.payload
      }
    },
    setPermissionLoading: (state, action: PayloadAction<boolean>) => {
      state.permissions.isLoading = action.payload
    },
    setPermissions: (
      state,
      action: PayloadAction<{
        interfaces: InterfaceData[]
        interfacesByOrganization: InterfaceData[]
      }>,
    ) => {
      state.permissions.interfaces = action.payload.interfaces
      state.permissions.interfacesByOrganization =
        action.payload.interfacesByOrganization
      state.permissions.isLoading = false
      state.permissions.lastFetched = Date.now()
    },
    setIsViewOnly: (state, action: PayloadAction<boolean>) => {
      state.permissions.isViewOnly = action.payload
    },
    updatePermissions: (
      state,
      action: PayloadAction<Partial<UserPermissions>>,
    ) => {
      state.permissions = { ...state.permissions, ...action.payload }
    },
    clearUser: (state) => {
      state.user = null
      state.permissions = initialPermissions
      state.error = null
      state.isLoading = false
    },
    resetUserState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPermissions.pending, (state) => {
        state.permissions.isLoading = true
      })
      .addCase(fetchUserPermissions.fulfilled, (state, action) => {
        state.permissions.interfaces = action.payload.interfaces
        state.permissions.interfacesByOrganization =
          action.payload.interfacesByOrganization

        /**
         * @todo:
         * comment/uncomment to TEST only.
         */
        // state.permissions.isViewOnly = true
        state.permissions.isViewOnly = action.payload.isViewOnly

        state.permissions.isLoading = false
        state.permissions.lastFetched = Date.now()
      })
      .addCase(fetchUserPermissions.rejected, (state, action) => {
        state.permissions.isLoading = false
        state.error =
          (action.payload as string) || 'Failed to fetch permissions'
      })
  },
})

export const {
  setLoading,
  setError,
  setUser,
  updateUserProfile,
  updateUserMeasure,
  updateUserTimezone,
  updateUserLanguage,
  setPermissionLoading,
  setPermissions,
  setIsViewOnly,
  updatePermissions,
  clearUser,
  resetUserState,
} = userSlice.actions

export default userSlice.reducer
